import Mapbox from 'mapbox-gl'
import RasterTileDataStream from '../../map/stream/RasterTileDataStream'
import RasterTileLayer from '../../map/layer/RasterTileLayer'
import ShapeLayer from '../../map/layer/ShapeLayer'
import AnnotationLayer from '../../map/layer/AnnotationLayer'
import LabelAnnotationLayer from '../../map/layer/LabelAnnotationLayer'
import UrlUtils from '../../utils/UrlUtils'
import Shape from '@/map/element/Shape'
import Annotation from '@/map/element/Annotation'
import ShapeDataStream from '../../map/stream/ShapeDataStream'
import AnnotationDataStream from '../../map/stream/AnnotationDataStream'
import MapObject from '../../model/MapObject'
import ProxiedGeolocateControl from './ProxiedGeolocateControl'

export default class MapboxMapControl {
    constructor(mapSpec, container) {
        this.mapSpec = mapSpec;
        this.container = container;

        this.baseOSMZoom = mapSpec.baseOSMZoom;
        this.projectionProxy = mapSpec.projectionProxy;

        this._overrideFetch();
    }

    load() {
        if (this.projectionProxy) {
            const worldInitialZoom = this.projectionProxy.toWorldZoom(this.mapSpec.initialOSMZoom);
            const worldMinZoom = this.projectionProxy.toWorldZoom(this.mapSpec.minOSMZoom);
            const worldMaxZoom = this.projectionProxy.toWorldZoom(this.mapSpec.maxOSMZoom);
            const worldInitialCenter = this.projectionProxy.toWorldLocation(this.mapSpec.initialCenterLocation);
            const worldTopLeftCorner = this.mapSpec.topLeftCorner;
            const worldBottomRightCorner = this.mapSpec.bottomRightCorner;

            this.map = new Mapbox.Map({
                container: this.container,
                style: {
                    version: 8,
                    sources: {},
                    layers: [],
                    glyphs: "https://api.maptiler.com/fonts/{fontstack}/{range}.pbf?key=hdfArA9PrQW6jayQHqZ9"
                },
                center: [worldInitialCenter.longitude, worldInitialCenter.latitude],
                zoom: worldInitialZoom,
                minZoom: worldMinZoom,
                maxZoom: worldMaxZoom,
                maxBounds: [
                    [worldTopLeftCorner.longitude, worldBottomRightCorner.latitude],
                    [worldBottomRightCorner.longitude, worldTopLeftCorner.latitude]
                ]
            });

            this.map.dragRotate.disable();
            this.map.touchZoomRotate.disableRotation();

            const geolocate = new ProxiedGeolocateControl({
                    positionOptions: {
                    enableHighAccuracy: true
                },
                trackUserLocation: true,
                proxy: this.projectionProxy,
            });

            this.map.addControl(geolocate, "bottom-right");

            this.map.on("load", () => {
                const stylesToLoad = {};

                for (var dataStream of this.mapSpec.dataStreams) {
                    if (dataStream instanceof RasterTileDataStream) {
                        this.map.addSource(dataStream.id, {
                            type: "raster",
                            tiles: dataStream.tilesUrls,
                            tileSize: dataStream.tileSize,
                            minzoom: this.projectionProxy.toWorldZoom(dataStream.minZoom),
                            maxzoom: this.projectionProxy.toWorldZoom(dataStream.maxZoom),
                            scheme: dataStream.scheme
                        });
                    } else if (dataStream instanceof ShapeDataStream || dataStream instanceof AnnotationDataStream) {
                        this.map.addSource(dataStream.id, {
                            type: "geojson",
                            data: {
                                type: "FeatureCollection",
                                features: []
                            }
                        });

                        if (dataStream.styles) {
                            for (const mapElementStyle of dataStream.styles) {
                                stylesToLoad[mapElementStyle.id] = mapElementStyle;
                            }
                        }
                    }
                }

                for (var layer of this.mapSpec.layers) {
                    if (layer instanceof RasterTileLayer) {
                        this.map.addLayer({
                            id: layer.id,
                            type: "raster",
                            source: layer.dataStream.id
                        });
                    } else if (layer instanceof ShapeLayer) {
                        const normalLayer = {
                            id: layer.id,
                            type: "line",
                            source: layer.dataStream.id,
                            layout: {
                                "line-join": "round",
                                "line-cap": "round",
                                "visibility": "visible"
                            },
                            paint: {
                                "line-color": ["get", layer.propertiesDictionary.strokeColor],
                                "line-width": layer.getStrokeWidthExpression(false, this.projectionProxy)
                            },
                            filter: [
                                "all",
                                ["==", "hasScalableStrokeWidth", false],
                                ["==", "hasStrokePattern", false]
                            ]
                        };

                        const fillLayer = {
                            id: layer.id + "_fill",
                            type: "fill",
                            source: layer.dataStream.id,
                            layout: {
                                "visibility": "visible"
                            },
                            paint: {
                                "fill-color": ["get", layer.propertiesDictionary.fillColor],
                                "fill-opacity": ["get", layer.propertiesDictionary.fillOpacity]
                            },
                            filter: ["==", "hasFillColor", true]
                        }

                        const scalableStrokeLayer = {
                            id: layer.id + "_scalable",
                            type: "line",
                            source: layer.dataStream.id,
                            layout: {
                                "line-join": "round",
                                "line-cap": "round",
                                "visibility": "visible"
                            },
                            paint: {
                                "line-color": ["get", layer.propertiesDictionary.strokeColor],
                                "line-width": layer.getStrokeWidthExpression(true, this.projectionProxy)
                            },
                            filter: [
                                "all",
                                ["==", "hasScalableStrokeWidth", true],
                                ["==", "hasStrokePattern", false]
                            ]
                        };

                        const strokePatternLayer = {
                            id: layer.id + "_strokePattern",
                            type: "line",
                            source: layer.dataStream.id,
                            layout: {
                                "line-join": "round",
                                "line-cap": "round",
                                "visibility": "visible"
                            },
                            paint: {
                                "line-color": ["get", layer.propertiesDictionary.strokeColor],
                                "line-width": layer.getStrokeWidthExpression(false, this.projectionProxy),
                                "line-dasharray": [3, 2]
                            },
                            filter: [
                                "all",
                                ["==", "hasScalableStrokeWidth", false],
                                ["==", "hasStrokePattern", true]
                            ]
                        };

                        const scalableStrokePatternLayer = {
                            id: layer.id + "_scalableStrokePattern",
                            type: "line",
                            source: layer.dataStream.id,
                            layout: {
                                "line-join": "round",
                                "line-cap": "round",
                                "visibility": "visible"
                            },
                            paint: {
                                "line-color": ["get", layer.propertiesDictionary.strokeColor],
                                "line-width": layer.getStrokeWidthExpression(true, this.projectionProxy),
                                "line-dasharray": [3, 2, 3, 2]
                            },
                            filter: [
                                "all",
                                ["==", "hasScalableStrokeWidth", true],
                                ["==", "hasStrokePattern", true]
                            ]
                        }

                        this.map.addLayer(fillLayer);
                        this.map.addLayer(normalLayer);
                        this.map.addLayer(scalableStrokeLayer);
                        this.map.addLayer(strokePatternLayer);
                        this.map.addLayer(scalableStrokePatternLayer);
                    } else if (layer instanceof LabelAnnotationLayer) {
                        this.map.addLayer({
                            id: layer.id,
                            type: "symbol",
                            source: layer.dataStream.id,
                            layout: {
                                "text-field": layer.getLabelExpression(),
                                "text-allow-overlap": true,
                                "text-size": 13
                            },
                            paint: {
                                "text-color": layer.getLabelColorExpression(),
                                "text-halo-color": layer.getLabelHaloColorExpression(),
                                "text-halo-width": 3
                            },
                            filter: ["==", "scalable", false]
                        });

                        this.map.addLayer({
                            id: layer.id + "_scalable",
                            type: "symbol",
                            source: layer.dataStream.id,
                            layout: {
                                "text-field": layer.getLabelExpression(),
                                "text-allow-overlap": true,
                                "text-size": 13
                            },
                            paint: {
                                "text-color": layer.getLabelColorExpression(),
                                "text-halo-color": layer.getLabelHaloColorExpression(),
                                "text-halo-width": 3
                            },
                            filter: ["==", "scalable", true]
                        });
                    } else if (layer instanceof AnnotationLayer) {
                        this.map.addLayer({
                            id: layer.id,
                            type: "symbol",
                            source: layer.dataStream.id,
                            layout: {
                                "icon-image": layer.getIconExpression(this.projectionProxy), //["get", "styleId"],
                                "icon-size": layer.getIconScaleExpression(false, this.projectionProxy),
                                "icon-ignore-placement": true,
                                "icon-allow-overlap": true,
                                "icon-offset": layer.getIconOffsetArrayExpression(false, this.projectionProxy)
                            },
                            filter: ["==", "scalable", false]
                        });

                        this.map.addLayer({
                            id: layer.id + "_scalable",
                            type: "symbol",
                            source: layer.dataStream.id,
                            layout: {
                                "icon-image": ["get", "styleId"],
                                "icon-size": layer.getIconScaleExpression(true, this.projectionProxy),
                                "icon-ignore-placement": true,
                                "icon-allow-overlap": true,
                                "icon-offset": layer.getIconOffsetArrayExpression(true, this.projectionProxy)
                            },
                            filter: ["==", "scalable", true]
                        });
                    }

                    if (layer instanceof LabelAnnotationLayer || layer instanceof AnnotationLayer || layer instanceof ShapeLayer) {
                        const thisLayer = layer;
                        thisLayer.observe().subscribe((updateInfo) => {
                            const mapElements = updateInfo.mapElements;
                            const visible = updateInfo.visible;

                            const features = [];
                            if (mapElements) {
                                for (const mapElement of mapElements) {
                                    features.push(mapElement.toGeoJSONFeature(this.baseOSMZoom, this.projectionProxy));
                                }
                            }

                            const source = this.map.getSource(thisLayer.id);
                            if (source) {
                                const geoJSONDocument = {
                                    type: "FeatureCollection",
                                    features: features
                                };
                                source.setData(geoJSONDocument);
                            }

                            const suffixes = ["", "_fill", "_scalable", "_strokePattern", "_scalableStrokePattern"]
                            for (const suffix of suffixes) {
                              const layerId = thisLayer.id + suffix;
                              const layer = this.map.getLayer(layerId);
                              if (layer) {
                                this.map.setLayoutProperty(layerId, "visibility", visible ? "visible" : "none");
                              }
                            }

                            if (thisLayer instanceof ShapeLayer) {

                                //console.log("process dashed lines");
                            }
                        });
                    }

                    /* eslint-disable no-unused-vars */
                    var ro = new ResizeObserver(entries => {
                        if (this.resizeTimeout) {
                            clearTimeout(this.resizeTimeout);
                        }
                        this.resizeTimeout = setTimeout(() => {
                            this.map.resize();
                        }, 0);
                    });
                    /* eslint-enable no-unused-vars */

                    // Observe one or multiple elements
                    // ro.observe(this.container);
                }

                this.map.on("mousemove", (event) => {
                    var bbox = [
                        [event.point.x - 5, event.point.y - 5],
                        [event.point.x + 5, event.point.y + 5]
                    ];

                    var features = this.map.queryRenderedFeatures(bbox);
                    if (this.mouseMoveListener) {
                        this.mouseMoveListener(features);
                    }
                });

                this.map.on("click", (event) => {
                    var bbox = [
                        [event.point.x - 5, event.point.y - 5],
                        [event.point.x + 5, event.point.y + 5]
                    ];

                    var features = this.map.queryRenderedFeatures(bbox);
                    this.clickListener(features);
                });

                stylesToLoad[MapObject.PoiType.EMERGENCY] = { id: MapObject.PoiType.EMERGENCY, url: require("@/assets/pins/EmergencyPin.png") };
                stylesToLoad[MapObject.PoiType.TERRAIN_REPORT] = { id: MapObject.PoiType.TERRAIN_REPORT, url: require("@/assets/pins/TerrainReportPin.png") };
                stylesToLoad[MapObject.PoiType.NORMAL] = { id: MapObject.PoiType.NORMAL, url: require("@/assets/pins/NormalPin.png") };

                for (const styleId in stylesToLoad) {
                    const mapElementStyle = stylesToLoad[styleId];
                    if (mapElementStyle.url) {
                        this.map.loadImage(mapElementStyle.url, (error, image) => {
                            if (error) {
                                console.log(error);
                            } else {
                                this.map.addImage(mapElementStyle.id, image);
                            }
                        });
                    }
                }
            });
        }
    }

    zoomIn() {
        if (this.map) {
            this.map.zoomIn()
        }
    }

    zoomOut() {
        if (this.map) {
            this.map.zoomOut()
        }
    }

    enableScrollZoom() {
        if (this.map) {
            this.map.scrollZoom.enable();
        }
    }

    disableScrollZoom() {
        if (this.map) {
            this.map.scrollZoom.disable();
        }
    }

    onClick(listener) {
        this.clickListener = listener;
    }

    onMouseMove(listener) {
        this.mouseMoveListener = listener;
    }

    centerOnMapElement(mapElement) {
        if (mapElement instanceof Annotation) {
            const worldLocation = mapElement.getWorldLocation(this.projectionProxy);
            this.map.flyTo({ center: [worldLocation.longitude, worldLocation.latitude] });
        } else if (mapElement instanceof Shape) {
            const bounds = mapElement.getBounds();
            this.centerOnBounds(bounds[0], bounds[1]);
        }
    }

    centerOnLocation(location) {
        const worldLocation = this.projectionProxy.toWorldLocation(location)
        this.map.flyTo({ center: [worldLocation.longitude, worldLocation.latitude] });
    }

    centerOnBounds(topLeftLocation, bottomRightLocation) {
        const worldTopLeftLocation = this.projectionProxy.toWorldLocation(topLeftLocation);
        const worldBottomRightLocation = this.projectionProxy.toWorldLocation(bottomRightLocation);

        this.map.fitBounds([
            [worldTopLeftLocation.longitude, worldTopLeftLocation.latitude],
            [worldBottomRightLocation.longitude, worldBottomRightLocation.latitude]
        ]);
    }

    showSelectionMarkerForMapElement(mapElement) {
        const worldLocation = mapElement.getWorldLocation(this.projectionProxy);
        if (!this.selectionMarker) {
            this.selectionMarker = new Mapbox.Marker();
        }
        this.selectionMarker.remove();
        this.selectionMarker.setLngLat([worldLocation.longitude, worldLocation.latitude]).addTo(this.map);
    }

    showSelectionMarker(location) {
        const worldLocation = this.projectionProxy.toWorldLocation(location);

        if (!this.selectionMarker) {
            this.selectionMarker = new Mapbox.Marker();
        }
        this.selectionMarker.remove();
        this.selectionMarker.setLngLat([worldLocation.longitude, worldLocation.latitude]).addTo(this.map);
    }

    hideSelectionMarker() {
        if (this.selectionMarker) {
            this.selectionMarker.remove();
            this.selectionMarker = null;
        }
    }

    changeMouseCursor(cursor) {
        this.map.getCanvas().style.cursor = cursor;
    }

    _overrideFetch() {
        /* eslint-disable no-global-assign */
        var oldFetch = fetch;
        fetch = (url, options) => {
            if (url) {
                var urlToCall = url.url;
                if (urlToCall == undefined && typeof(url) == "string") {
                    urlToCall = url;
                }

                if (this.mapSpec) {
                    for (var dataStream of this.mapSpec.dataStreams) {
                        if (dataStream instanceof RasterTileDataStream) {
                            const tilesUrls = dataStream.tilesUrls;
                            if (tilesUrls) {
                                var foundUrl = null;
                                for (const tilesUrl of tilesUrls) {
                                    const urlStart = tilesUrl.substring(0, tilesUrl.indexOf("{"));
                                    if (url && url.url && url.url.indexOf(urlStart) == 0) {
                                        foundUrl = tilesUrl;
                                        break;
                                    }
                                }

                                if (foundUrl) {
                                    const tile = UrlUtils.extractOSMTileFromUrl(url.url, foundUrl);
                                    if (tile) {
                                        const mapTile = this.projectionProxy.toMapOSMTile(tile);
                                        urlToCall = UrlUtils.buildUrlFromOSMTile(mapTile, foundUrl);
                                    }
                                }
                            }
                        }
                    }
                }

                return oldFetch(urlToCall, options);
            } else {
                return oldFetch(url, options);
            }
        }
        /* eslint-enable no-global-assign */
    }
}
